import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  ClipboardIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getImageAssetsApiImagesImageIdAssetsGetOptions } from "../generated/api/@tanstack/react-query.gen";
import type { AssetResponse, HydratedImage } from "../generated/api/types.gen";
import {
  buildDisplayParameterGroups,
  formatParametersAsText,
} from "../lib/display-utils";
import { isColorParameter, isHexColor } from "../lib/parameter-label";
import { Button } from "./Button";

interface GalleryDetailsPanelProps {
  /** Full-page session view keeps top spacer; embedded sits under gallery breadcrumbs. */
  variant?: "page" | "embedded";
  image: HydratedImage;
  currentIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
  hasMultipleIterations: boolean;
}

interface NavigationHeaderProps {
  image: HydratedImage;
  currentIndex: number;
  totalCount: number;
  hasMultipleIterations: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

function NavigationHeader({
  image,
  currentIndex,
  totalCount,
  hasMultipleIterations,
  onPrevious,
  onNext,
}: NavigationHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="text-heading-modal">
        {image.design_subject || "Design Details"}
      </div>
      {hasMultipleIterations && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrevious}
            className="w-8 h-8 p-2 bg-[var(--bg-field)] hover:bg-[var(--stroke-field)] rounded-lg flex items-center justify-center transition text-[var(--text-primary)]"
          >
            <CaretLeftIcon size={16} />
          </button>
          <span className="text-[var(--text-secondary)] text-sm min-w-[50px] text-center">
            {currentIndex + 1}/{totalCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            className="w-8 h-8 p-2 bg-[var(--bg-field)] hover:bg-[var(--stroke-field)] rounded-lg flex items-center justify-center transition text-[var(--text-primary)]"
          >
            <CaretRightIcon size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

/** A compact version of the gallery asset card: a square swatch/thumbnail on
 *  the left and a white panel with the asset name on the right. */
function AssetChip({ asset }: { asset: AssetResponse }) {
  const showColor = asset.type === "color" && isHexColor(asset.value);
  return (
    <div className="flex overflow-hidden rounded-md shadow-sm">
      {asset.type === "image" ? (
        <img
          src={asset.value}
          alt=""
          className="h-8 w-8 shrink-0 object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className="h-8 w-8 shrink-0"
          style={{
            backgroundColor: showColor ? asset.value : "var(--bg-field)",
          }}
        />
      )}
      <div className="min-w-0 flex-1 bg-white px-2.5 py-2">
        <span className="block min-w-0 truncate text-[13px] font-semibold leading-4 text-[#20262C]">
          {asset.name}
        </span>
      </div>
    </div>
  );
}

/** Assets used within this specific image (iteration), derived from the
 *  asset_id saved on its parameters. Hidden when none. */
function AssetsSection({ assets }: { assets: AssetResponse[] }) {
  if (assets.length === 0) return null;
  return (
    <div className="mb-6">
      <div className="text-heading-category mb-2">Assets</div>
      <div className="grid grid-cols-2 gap-2">
        {assets.map((asset) => (
          <AssetChip key={asset.id} asset={asset} />
        ))}
      </div>
    </div>
  );
}

type DisplayGroup = ReturnType<typeof buildDisplayParameterGroups>[number];
type DisplayParam = DisplayGroup["parameters"][number];

/** Mirrors the refinement input: a hex value shows a tile and, when it came
 *  from a saved asset, that asset's name instead of the raw hex. */
function ParameterValue({ param }: { param: DisplayParam }) {
  const isColor = isColorParameter({ value: param.value, key: param.name });
  const text = param.assetName || param.value || "N/A";
  return (
    <span className="flex min-w-0 items-center gap-2">
      {isColor && (
        <span
          aria-hidden="true"
          className="w-4 h-4 rounded shrink-0 border border-[var(--stroke-field)]"
          style={{ backgroundColor: param.value }}
        />
      )}
      <span className="text-[var(--text-secondary)] text-input-field truncate">
        {text}
      </span>
    </span>
  );
}

interface ParametersSectionProps {
  displayGroups: DisplayGroup[];
  copySuccess: boolean;
  onCopy: () => void;
}

function ParametersSection({
  displayGroups,
  copySuccess,
  onCopy,
}: ParametersSectionProps) {
  if (displayGroups.length === 0) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-heading-modal">Parameters</div>
        <Button onClick={onCopy} variant="primary">
          {copySuccess ? <CheckIcon size={16} /> : <ClipboardIcon size={16} />}
        </Button>
      </div>
      <div className="space-y-4">
        {displayGroups.map((group) => (
          <div key={group.name}>
            <div className="text-heading-category mb-2">{group.name}</div>
            <div className="space-y-2 pl-3 border-l-2 border-[var(--stroke-vertical-rule)] ml-0.5">
              {group.parameters.map((param) => (
                <div
                  key={`${group.name}-${param.name}`}
                  className="flex items-center justify-between rounded-lg bg-[var(--bg-field)] p-3"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-[var(--text-primary)] text-label-field shrink-0">
                      {param.name}
                    </span>
                    <ParameterValue param={param} />
                  </div>
                  {param.isAuto && (
                    <div className="flex items-center gap-1 text-[var(--text-secondary)] text-xs">
                      <span>Auto</span>
                      <SparkleIcon size={16} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReferenceImageThumbnails({ image }: { image: HydratedImage }) {
  if (!(image.reference_image_url || image.reference_image_url_2)) return null;
  return (
    <div className="mb-6">
      <div className="text-heading-category mb-2">Reference Images</div>
      <div className="flex gap-2">
        {image.reference_image_url && (
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[var(--stroke-field)]">
            <img
              src={image.reference_image_url}
              alt="Reference 1"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {image.reference_image_url_2 && (
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[var(--stroke-field)]">
            <img
              src={image.reference_image_url_2}
              alt="Reference 2"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Assets used by the image, plus whether to hide the reference-image
 *  thumbnails: swatches attached to parameters are assets, not references,
 *  so when an image without a refinement prompt carries image assets its
 *  persisted reference slots hold those swatches and would duplicate the
 *  asset cards. User-uploaded references from prompt refinement (which sets
 *  refinement_prompt) stay visible. */
function useGalleryImageAssets(image: HydratedImage) {
  const query = useQuery(
    getImageAssetsApiImagesImageIdAssetsGetOptions({
      path: { image_id: image.id },
    }),
  );
  const assets = query.data ?? [];
  return {
    assets,
    hideReferenceImages:
      query.isPending ||
      (!image.refinement_prompt &&
        assets.some((asset) => asset.type === "image")),
  };
}

export function GalleryDetailsPanel({
  variant = "page",
  image,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
  hasMultipleIterations,
}: GalleryDetailsPanelProps) {
  const [copySuccess, setCopySuccess] = useState(false);
  const { assets, hideReferenceImages } = useGalleryImageAssets(image);

  const displayGroups = useMemo(
    () => buildDisplayParameterGroups(image.parameter_groups),
    [image.parameter_groups],
  );

  const handleCopyParameters = async () => {
    const text = formatParametersAsText(displayGroups);
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const isEmbedded = variant === "embedded";

  return (
    <div
      className={`flex min-h-0 flex-col ${isEmbedded ? "w-full" : "w-[420px]"}`}
    >
      {!isEmbedded && (
        <div className="pb-2" aria-hidden="true">
          <div className="h-[20px]" />
        </div>
      )}
      <div
        className={`scrollbar-hide flex-1 overflow-y-auto ${isEmbedded ? "rounded-xl bg-transparent px-5 pb-5 pt-5" : "rounded-2xl bg-[var(--bg-modal)] px-6 pb-6 pt-6"}`}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <NavigationHeader
          image={image}
          currentIndex={currentIndex}
          totalCount={totalCount}
          hasMultipleIterations={hasMultipleIterations}
          onPrevious={onPrevious}
          onNext={onNext}
        />

        {image.user_prompt && !image.parent_image_id && (
          <div className="mb-6">
            <div className="text-heading-category mb-2">User Input</div>
            <p className="text-body-small">{image.user_prompt}</p>
          </div>
        )}

        {image.refinement_prompt && (
          <div className="mb-6">
            <div className="text-heading-category mb-2">Refinement Input</div>
            <p className="text-body-small">{image.refinement_prompt}</p>
          </div>
        )}

        {!hideReferenceImages && <ReferenceImageThumbnails image={image} />}

        <div className="mb-6">
          <div className="text-heading-category mb-2">Generated</div>
          <p className="text-body-small">
            {new Date(image.generated_at).toLocaleString()}
          </p>
        </div>

        <AssetsSection assets={assets} />

        <ParametersSection
          displayGroups={displayGroups}
          copySuccess={copySuccess}
          onCopy={handleCopyParameters}
        />
      </div>
    </div>
  );
}
